import type { AriaSpan } from "@alm/domain/legal";
import { LegalSpans, type LegalLink } from "@/components/marketing/site/legal/legal-spans";

const BULLET = /^- /;

/** A `>` block of the contract: the plain-language summary drawn as a box, or a clause's lettered
 * sub-clauses set in from the margin. */
export function LegalQuote({
  paragraphs,
  link,
  linkBaa,
}: {
  paragraphs: readonly AriaSpan[][];
  link: LegalLink;
  linkBaa: boolean;
}) {
  const [first, ...rest] = paragraphs;
  const summary = first?.[0]?.k === "strong" && first[0].text === "The basics";
  if (!summary) {
    return (
      <div className="flex flex-col gap-3 border-l border-border pl-5">
        {paragraphs.map((spans, index) => (
          // oxlint-disable-next-line react/no-array-index-key -- legal text rendered once from static data, never reordered
          <p key={`clause-${index}`}>
            <LegalSpans spans={spans} link={link} linkBaa={linkBaa} />
          </p>
        ))}
      </div>
    );
  }
  return (
    <aside
      className="rounded-xl border border-border bg-card p-6"
      aria-label="The basics"
      data-testid="legal-summary"
    >
      <p>
        <LegalSpans spans={first} link={link} linkBaa={linkBaa} />
      </p>
      <ul className="mt-4 flex list-disc flex-col gap-2 pl-5">
        {rest.map((spans, index) => {
          const [lead, ...tail] = spans;
          const trimmed =
            lead?.k === "text"
              ? [{ ...lead, text: lead.text.replace(BULLET, "") }, ...tail]
              : spans;
          return (
            // oxlint-disable-next-line react/no-array-index-key -- legal text rendered once from static data, never reordered
            <li key={`basic-${index}`}>
              <LegalSpans spans={trimmed} link={link} linkBaa={linkBaa} />
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
